import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Swal from 'sweetalert2/src/sweetalert2';
import { isNull } from 'lodash';
import useAcl from '@hooks/useAcl';

import { VIEW_LISTING_CLICKED } from '@redux/constants/ActionTypes';
import { loadListingToCopyById } from '@redux/modules/listing';
import { loadCityNames, loadFormDefinition } from '@redux/modules/formGeneration';

import Panel from '@components/smartAdminUI/Panel';
import ListingsTable from '@components/table/ListingsTable';
import SearchListings from '@components/form/forms/SearchListings';
import Button from '@components/smartAdminUI/Button';
import ListingsDropdown from '@components/dropdowns/ListingsDropdown';

const SearchListingsPage = () => {
  const updatedResults = []; // temp array to hold new values before adding to state
  const lookupCities = {
    lookupnames: ['Lookup_Property_Listing_City'],
  }; // hardcoded since cities lookup string is not changing

  const dispatch = useDispatch();
  const history = useHistory();
  const cityNames = useSelector(state => state.formGeneration.cityNames);
  const searchResults = useSelector(state => state.listing.searchListingsResults);

  const { mainActions } = useAcl();

  const viewListing = displayListing => dispatch({
    type: VIEW_LISTING_CLICKED, data: displayListing,
  });

  const [displayResults, setDisplayResults] = useState([]);

  // retrieves city lookup table values on mount
  useEffect(() => {
    dispatch(loadCityNames(lookupCities));
  }, []);

  /*
    listener for search listings results and city name redux states
    finds city name that matches city code in lookup tables
    if the city item is active, city codes are replaced with their description (actual city name)
    updated values are saved in displayResults state for display
   */
  useEffect(() => {
    if (searchResults?.length > 0) {
      searchResults.map(listing => {
        if (listing?.City && cityNames) {
          const cityItem = cityNames[listing.City];
          if (cityItem?.Active) {
            updatedResults.push({
              ...listing,
              City: cityItem.Description,
            });
          }
          setDisplayResults([...updatedResults]);
        } else { // Listings could be entirely blank but would still need to show up in the results table
          let newResults;
          if (listing?.City != null) {
            newResults = searchResults;
          } else {
            updatedResults.push({
              ...listing,
              City: '',
            });
            newResults = updatedResults;
          }
          setDisplayResults([...newResults]);
        }
        return null;
      });
    } else {
      setDisplayResults([]);
    }
  }, [searchResults, cityNames]);

  // Build where clause with UniqueID to retrieve records for
  const buildWhereClause = uniqueID => {
    const whereClause = {
      where: {
        clause: [
          {
            name: 'UniqueID',
            value: uniqueID,
          },
        ],
      },
    };
    return whereClause;
  };

  const listingSelected = row => {
    const currentListing = displayResults.find(listing => listing.UniqueID === row.UniqueID);
    viewListing(currentListing);
  };

  const copyClicked = row => {
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Copying Listing...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-copy fa-stack-1x"></i></span>',
      showConfirmButton: false,
      timer: 2000,
    });
    const currentListing = displayResults.find(listing => listing.UniqueID === row.UniqueID);
    dispatch(loadFormDefinition('create', 'listings', currentListing?.PropertyType?.toLowerCase(), null, 'listing', true));
    const copyRequest = buildWhereClause(row.UniqueID);
    copyRequest.formName = currentListing?.PropertyType?.toLowerCase();
    dispatch(loadListingToCopyById(copyRequest));
  };

  const auditClicked = row => {
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Loading Listing Audit Log...',
      title: '<span class="fa-stack fa-2x text-secondary m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-history fa-stack-1x"></i></span>',
      showConfirmButton: false,
      timer: 2000,
    });
    history.push(`/audit?ListingID=${row.ListingID}`);
  };

  return (
    <>
      <div className="subheader">
        <h1 className="subheader-title">
          <i className="subheader-icon fal fa-search mr-2" />
          Search
          <span className="fw-300 ml-2">Listings</span>
        </h1>
      </div>
      <div className="row">
        <div className="col-xl-12">
          <Panel
            header="Search Criteria"
            customClass="searchCriteria"
            panelToolbar={(
              <>
                <div className="btn-group">
                  {mainActions.ListingsCreate && (
                    <Button
                      variant="primary dropdown-toggle"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      <i className="fas fa-plus mr-2" />
                      Create Listing
                    </Button>
                  )}
                  <ListingsDropdown />
                </div>
              </>
            )}
          >
            <p className="text-center alert alert-info mt-0 mb-4 wobble-hor-bottom">
              <i className="far fa-exclamation-circle mr-2" />
              Please note that the search will only return listings you are authorized to see.
            </p>
            <SearchListings />
          </Panel>
        </div>
        {!isNull(searchResults) && (
        <div className="col-xl-12">
          <Panel header="Search Results" customClass="searchResults">
            <ListingsTable
              displayData={displayResults}
              onListingSelect={listingSelected}
              onCopyClick={copyClicked}
              onAuditClick={auditClicked}
            />
          </Panel>
        </div>
        )}
      </div>
    </>
  );
};

export default SearchListingsPage;
